import { createRouter, createWebHistory, type RouteRecordRaw, type RouteRecordSingleView } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { i18n, savedLocale, setLocale } from '@/locales'
import { getFeature, MK_LOCALES, type MkLocale } from '@/marketing/features'
import type { LegalName, MkPage } from '@/marketing/seo'
import { applySeo } from '@/marketing/useSeo'
import { isVerifyHost } from '@/lib/verifyHost'

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    requiresAuth?: boolean
    superAdmin?: boolean
    /** Public marketing page: locale comes from the URL prefix, SEO from seo.ts. */
    marketing?: boolean
    locale?: MkLocale
    mkPage?: MkPage
    /** Legal page / verifier: per-locale head from seo.ts LEGAL_PAGES. */
    legal?: LegalName
  }
}

// EN at /x, other locales at /<code>/x (see src/marketing/seo.ts).
const HomeView = () => import('@/views/HomeView.vue')
const FeaturesHubView = () => import('@/views/marketing/FeaturesHubView.vue')
const FeatureView = () => import('@/views/marketing/FeatureView.vue')
const PricingView = () => import('@/views/marketing/PricingView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const CmsPageView = () => import('@/cms/CmsPageView.vue')

const marketingRoutes: RouteRecordRaw[] = MK_LOCALES.flatMap((locale) => {
  const p = locale === 'en' ? '' : `/${locale}`
  const n = locale === 'en' ? '' : `.${locale}`
  const meta = (mkPage: MkPage) => ({ public: true, marketing: true, locale, mkPage })
  return [
    { path: p || '/', name: `home${n}`, component: HomeView, meta: meta('home') },
    { path: `${p}/features`, name: `features${n}`, component: FeaturesHubView, meta: meta('hub') },
    {
      path: `${p}/features/:slug`,
      name: `feature${n}`,
      component: FeatureView,
      meta: meta('feature'),
      // Unknown slug -> 404, URL kept.
      beforeEnter: (to) => getFeature(String(to.params.slug))
        ? true
        : { name: 'not-found', params: { pathMatch: to.path.slice(1).split('/') }, query: to.query, hash: to.hash },
    },
    { path: `${p}/pricing`, name: `pricing${n}`, component: PricingView, meta: meta('pricing') },
  ]
})

const VerifyView = () => import('@/views/VerifyView.vue')

// Legal pages, verifier, status and the GDPR form in every locale (/privacy, /pl/privacy, ...).
// Page text follows the URL locale like marketing pages; only LEGAL_PAGES ones are indexable.
const PUBLIC_PAGES: [name: string, view: RouteRecordSingleView['component'], legal?: LegalName][] = [
  ['privacy', () => import('@/views/PrivacyView.vue'), 'privacy'],
  ['terms', () => import('@/views/TermsView.vue'), 'terms'],
  ['dpa', () => import('@/views/DpaView.vue'), 'dpa'],
  ['subprocessors', () => import('@/views/SubprocessorsView.vue'), 'subprocessors'],
  ['verify', VerifyView, 'verify'],
  ['status', () => import('@/views/StatusView.vue')],
  ['rodo-request', () => import('@/views/RodoRequestView.vue')],
]
const publicPageRoutes: RouteRecordRaw[] = MK_LOCALES.flatMap((locale) => PUBLIC_PAGES.map(([name, component, legal]) => ({
  path: `${locale === 'en' ? '' : `/${locale}`}/${name}`,
  name: locale === 'en' ? name : `${name}.${locale}`,
  component,
  meta: { public: true, marketing: true, locale, legal },
})))

// verify.sign.yt: only the verify page, URL stays /<code>.
const verifyHostRoutes: RouteRecordRaw[] = [
  { path: '/', name: 'verify', component: VerifyView, meta: { public: true } },
  { path: '/:code([A-Za-z0-9]{16})', name: 'verify-code', component: VerifyView, meta: { public: true } },
  { path: '/l/:code([A-Za-z0-9]{16})', redirect: (to) => `/${String(to.params.code)}` },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  // Always start at the top of the new page on real navigations; restore the
  // saved position only on browser back/forward. Anchor links (#section) keep
  // their native jump behaviour. Without this every long page (privacy/terms/
  // dpa especially) opens scrolled to wherever the previous page was.
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (_to.hash) return { el: _to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
  routes: isVerifyHost ? verifyHostRoutes : [
    ...marketingRoutes,
    {
      path: '/login',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        { path: '', name: 'login', component: () => import('@/views/LoginView.vue') },
        { path: 'register', name: 'register', component: () => import('@/views/RegisterView.vue') },
      ],
    },
    {
      path: '/signup',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        { path: 'finish', name: 'signup-finish', component: () => import('@/views/SignupFinishView.vue') },
      ],
    },
    {
      // Not `public`: DemoView needs to know if a session already exists.
      path: '/demo',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [{ path: '', name: 'demo', component: () => import('@/views/DemoView.vue') }],
    },
    {
      // Demo -> real account: /login/register bounces signed-in users, so leave the demo first.
      path: '/demo/signup',
      name: 'demo-signup',
      component: NotFoundView,
      beforeEnter: async () => {
        const auth = useAuthStore()
        if (auth.user?.organization.demo_expires_at) await auth.logout()
        return { name: 'register' }
      },
    },
    {
      path: '/app',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
        { path: 'documents', name: 'documents', component: () => import('@/views/DocumentsListView.vue') },
        { path: 'inbox', name: 'inbox', component: () => import('@/views/InboxView.vue') },
        { path: 'documents/new', name: 'documents.new', component: () => import('@/views/NewDocumentView.vue') },
        { path: 'documents/import', name: 'documents.import', component: () => import('@/views/DocumensoImportView.vue') },
        { path: 'documents/:id', name: 'documents.detail', component: () => import('@/views/DocumentDetailView.vue') },
        { path: 'documents/:id/fields', name: 'documents.fields', component: () => import('@/views/DocumentFieldEditorView.vue') },
        { path: 'documents/:id/audit', name: 'documents.audit', component: () => import('@/views/DocumentAuditView.vue') },
        { path: 'templates', name: 'templates', component: () => import('@/views/TemplatesView.vue') },
        { path: 'templates/:id/fields', name: 'templates.fields', component: () => import('@/views/DocumentFieldEditorView.vue') },
        { path: 'smart-docs', name: 'smart-docs', component: () => import('@/views/SmartDocsView.vue') },
        { path: 'smart-docs/blueprints/new', name: 'smart-docs.blueprint.new', component: () => import('@/views/SmartBlueprintEditorView.vue') },
        { path: 'smart-docs/blueprints/:id', name: 'smart-docs.blueprint', component: () => import('@/views/SmartBlueprintEditorView.vue') },
        { path: 'smart-docs/blueprints/:id/pdf', name: 'smart-docs.blueprint.pdf', component: () => import('@/views/SmartPdfBlueprintEditorView.vue') },
        { path: 'smart-docs/packages/:id/fill', name: 'smart-docs.fill', component: () => import('@/views/SmartPackageFillView.vue') },
        { path: 'team', name: 'team', component: () => import('@/views/TeamView.vue') },
        { path: 'contacts', name: 'contacts', component: () => import('@/views/ContactsView.vue') },
        { path: 'settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
        { path: 'admin', name: 'admin', component: () => import('@/views/AdminDashboardView.vue'), meta: { superAdmin: true } },
        { path: 'admin/organizations', name: 'admin.organizations', component: () => import('@/views/AdminView.vue'), meta: { superAdmin: true } },
        { path: 'admin/users', name: 'admin.users', component: () => import('@/views/AdminUsersView.vue'), meta: { superAdmin: true } },
        { path: 'admin/identity', name: 'admin.identity', component: () => import('@/views/AdminIdentityView.vue'), meta: { superAdmin: true } },
        { path: 'admin/sms', name: 'admin.sms', component: () => import('@/views/AdminSmsView.vue'), meta: { superAdmin: true } },
        { path: 'admin/trust-providers', name: 'admin.trust-providers', component: () => import('@/views/AdminTrustProvidersView.vue'), meta: { superAdmin: true } },
        { path: 'admin/email', name: 'admin.email', component: () => import('@/views/AdminEmailView.vue'), meta: { superAdmin: true } },
        { path: 'admin/integrations', name: 'admin.integrations', component: () => import('@/views/AdminIntegrationsView.vue'), meta: { superAdmin: true } },
        { path: 'admin/storage', name: 'admin.storage', component: () => import('@/views/AdminStorageView.vue'), meta: { superAdmin: true } },
        { path: 'admin/translations', name: 'admin.translations', component: () => import('@/views/AdminTranslationsView.vue'), meta: { superAdmin: true } },
        { path: 'admin/platform', name: 'admin.platform', component: () => import('@/views/AdminPlatformView.vue'), meta: { superAdmin: true } },
        { path: 'admin/ai', name: 'admin.ai', component: () => import('@/views/AdminAiView.vue'), meta: { superAdmin: true } },
        { path: 'admin/landing', name: 'admin.landing', component: () => import('@/views/AdminLandingView.vue'), meta: { superAdmin: true } },
        { path: 'admin/landing/:id', name: 'admin.site.page', component: () => import('@/views/AdminSiteEditorView.vue'), meta: { superAdmin: true } },
        { path: 'admin/plans', name: 'admin.plans', component: () => import('@/views/AdminPlansView.vue'), meta: { superAdmin: true } },
        { path: 'admin/legal', name: 'admin.legal', component: () => import('@/views/AdminLegalView.vue'), meta: { superAdmin: true } },
        { path: 'profile', name: 'profile', component: () => import('@/views/ProfileView.vue') },
        { path: 'audit', name: 'audit', component: () => import('@/views/AuditLogView.vue') },
        { path: 'api-keys', name: 'api-keys', component: () => import('@/views/ApiKeysView.vue') },
        { path: 'webhooks', name: 'webhooks', component: () => import('@/views/WebhooksView.vue') },
        { path: 'documents/bulk', name: 'documents.bulk', component: () => import('@/views/BulkSendView.vue') },
        { path: 'analytics', name: 'analytics', component: () => import('@/views/AnalyticsView.vue') },
        { path: 'flows', name: 'flows', component: () => import('@/views/flows/FlowsView.vue') },
        { path: 'flows/runs/:runId', name: 'flows.run', component: () => import('@/views/flows/FlowRunView.vue') },
        { path: 'flows/:id', name: 'flows.builder', component: () => import('@/views/flows/FlowBuilderView.vue') },
        { path: 'billing', name: 'billing', component: () => import('@/views/BillingView.vue') },
        { path: 'campaigns', name: 'campaigns', component: () => import('@/views/campaigns/CampaignsView.vue') },
        { path: 'campaigns/new', name: 'campaigns.new', component: () => import('@/views/campaigns/CampaignWizardView.vue') },
        { path: 'campaigns/:id', name: 'campaigns.detail', component: () => import('@/views/campaigns/CampaignDetailView.vue') },
      ],
    },
    ...publicPageRoutes,
    { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/ForgotPasswordView.vue') },
    { path: '/reset-password', name: 'reset-password', component: () => import('@/views/ResetPasswordView.vue') },
    {
      // Identity-provider return: the provider never sees the signing token.
      path: '/sign/idv-return',
      name: 'sign-idv-return',
      meta: { public: true },
      redirect: () => {
        let tok: string | null = null
        try { tok = sessionStorage.getItem('signyt.idv_return') } catch { /* storage blocked */ }
        return tok ? `/sign/${tok}` : '/'
      },
    },
    {
      path: '/sign/:token',
      name: 'sign',
      component: () => import('@/views/SigningView.vue'),
      meta: { public: true },
    },
    {
      // iframe on an org-approved site (nginx sets per-org frame-ancestors).
      path: '/embed/sign/:token',
      name: 'embed-sign',
      component: () => import('@/views/SigningView.vue'),
      meta: { public: true, embed: true },
    },
    {
      // Legacy/custom-domain verify link; verify.sign.yt/{code} uses verifyHostRoutes.
      path: '/l/:code',
      name: 'verify-code',
      component: () => import('@/views/VerifyView.vue'),
      meta: { public: true },
    },
    {
      path: '/org-export',
      name: 'org-export',
      component: () => import('@/views/OrgExportDownloadView.vue'),
      meta: { public: true },
    },
    // Not `public`: these need to know whether the visitor is signed in.
    { path: '/invite', name: 'invite', component: () => import('@/views/InviteAcceptView.vue') },
    { path: '/verify-email', name: 'verify-email', component: () => import('@/views/VerifyEmailView.vue') },
    { path: '/t/public/:token', name: 'public-template', component: () => import('@/views/PublicTemplateView.vue'), meta: { public: true } },
    { path: '/c/:token', name: 'public-campaign', component: () => import('@/views/campaigns/PublicCampaignView.vue'), meta: { public: true } },
    // Website builder pages (src/cms). Static routes above always win.
    { path: '/site-preview/:token', name: 'cms-preview', component: CmsPageView, meta: { public: true } },
    ...MK_LOCALES.map((locale): RouteRecordRaw => ({
      path: `${locale === 'en' ? '' : `/${locale}`}/:slug([a-z0-9-]+)`,
      name: locale === 'en' ? 'cms-page' : `cms-page.${locale}`,
      component: CmsPageView,
      meta: { public: true, marketing: true, locale },
    })),
    { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView, meta: { public: true } },
  ],
})

// App locale before a marketing URL switched it; restored when leaving marketing.
let appLocale: string | null = null

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const isPublic = to.matched.some((r) => r.meta.public)
  if (!auth.loaded && !isPublic) await auth.bootstrap()
  // Marketing header needs auth state but must not wait for it.
  if (!auth.loaded && to.meta.marketing) void auth.bootstrap()
  // Marketing URL locale wins (not persisted); the app locale comes back on leaving.
  const cur: string = i18n.global.locale.value
  if (to.meta.locale && cur !== to.meta.locale) {
    appLocale ??= cur
    await setLocale(to.meta.locale, false)
  } else if (!to.meta.marketing && appLocale) {
    const back = savedLocale() ?? appLocale
    appLocale = null
    if (back !== cur) await setLocale(back, false)
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  // MFA gate (backend enforces too): enrol before any other app page.
  if (auth.user?.mfa_enrollment_required && to.meta.requiresAuth && to.name !== 'profile') {
    return { name: 'profile', query: { mfa: 'required' } }
  }
  if (to.meta.superAdmin && auth.user?.global_role !== 'SUPER_ADMIN') {
    return { path: '/app' }
  }
  // Platform super admins (not impersonating) land on Organizations —
  // they don't have org-level workflows (documents, templates, etc.).
  const isPlatformSuperAdmin =
    auth.user?.global_role === 'SUPER_ADMIN' && !auth.user?.is_impersonating
  if (isPlatformSuperAdmin && to.path === '/app') {
    return { path: '/app/admin' }
  }
  if ((to.name === 'login' || to.name === 'register') && auth.isAuthenticated) {
    return isPlatformSuperAdmin ? { path: '/app/admin' } : { path: '/app' }
  }
  return true
})

router.afterEach((to) => applySeo(to))

export default router
